import { Outlet, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCloudAuth } from "../../hooks/useCloudAuth";

export function DashboardLayout() {
  const { t } = useTranslation();
  const { user, loading, isAuthenticated, logout } = useCloudAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // KeycloakAuthGate handles unauthenticated/pending/banned at the top level.
  // This is a safety fallback — should not normally be reached.
  if (!isAuthenticated || !user) {
    return null;
  }

  const navLinks = [
    { to: "/dashboard", label: t('dashboard.layout.nav.dashboard'), end: true },
    { to: "/dashboard/self-hosted", label: t('dashboard.layout.nav.selfHosted') },
    { to: "/dashboard/settings", label: t('dashboard.layout.nav.settings') },
  ];

  if (user.role === "admin") {
    navLinks.push({ to: "/admin", label: t('dashboard.layout.nav.adminPanel'), end: false });
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="h-14 bg-[#111] border-b border-[#333] px-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <NavLink to="/" className="text-[#f5f5f5] font-bold text-sm">
            {t('dashboard.layout.brand')}
          </NavLink>
          <nav className="flex items-center gap-4">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-[#f5f5f5]" : "text-[#999] hover:text-[#f5f5f5]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[#999] text-sm">{user.email}</span>
          <button
            onClick={logout}
            className="text-[#999] text-sm hover:text-[#f5f5f5] transition-colors"
          >
            {t('dashboard.layout.logout')}
          </button>
        </div>
      </header>

      {/* Suspended banner */}
      {user.status === "suspended" && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2 text-center">
          <span className="text-yellow-400 text-sm">
            {t('dashboard.layout.suspended')}
          </span>
        </div>
      )}

      {/* Content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}
